import * as React from 'react';

import DeliverableMediaContextMenu from 'modules/deliverables/components/media/parts/deliverable-media-context-menu/DeliverableMediaContextMenu';
import ActionButton from 'components/buttons/action-button/ActionButton';

import { __t, applyTranslations } from 'utils/language';
import { DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO, DELIVERABLE_TYPE_ZALANDO_OUTFIT } from 'utils/deliverables';

import { Deliverable, DeliverableAttachment, DeliverableAttachmentApprovalStatus } from 'types';

import './deliverable-tab-footer.scss';

applyTranslations(require('./deliverable-tab-footer.i18n.json'));

type Props = {
  isEditMode: boolean,
  isForBrand?: boolean,
  isCcm?: boolean,
  deliverable: Deliverable,
  attachments: Array<DeliverableAttachment>,
  isModified?: boolean,
  isInsights?: boolean,
  allowContentMove?: boolean,
  captionStatus?: DeliverableAttachmentApprovalStatus,
  hasClientProvidedCaptionFeedback?: boolean,
  onCancelChanges: () => void,
  onSave: () => void,
  onSend?: () => void,
  onDownloadAttachments: (approvedOnly?: boolean | null) => void,
  onEditModeToggle: (isEditMode: boolean) => void,
  onResetApprovalStatus?: (deliverableAttachment: Array<DeliverableAttachment>) => void,
  onDeleteAttachments: (deliverableAttachment: Array<DeliverableAttachment>) => void,
  onApplyClientFeedback?: (deliverable: Deliverable, attachmentId?: number | null) => void,
  onOpenMoveContent?: (showMoveContent: boolean) => void,
  onReleaseToClient?: (showMoveContent: boolean) => void,
  onDeleteOutfits?: () => void
}

const msgKey = 'deliverable_tab_footer';

const DeliverableTabFooter = ({
  isEditMode,
  isModified,
  isInsights,
  isForBrand,
  isCcm,
  attachments = [],
  deliverable,
  allowContentMove,
  captionStatus,
  hasClientProvidedCaptionFeedback,
  onDeleteOutfits,
  onCancelChanges,
  onDeleteAttachments,
  onSave,
  onSend,
  onResetApprovalStatus,
  onDownloadAttachments,
  onEditModeToggle,
  onApplyClientFeedback,
  onOpenMoveContent,
  onReleaseToClient }: Props) => {
  const renderButtons = () => (
    <React.Fragment>
      {
        (isModified || (isForBrand && isEditMode)) && (
          <ActionButton
            color="tertiary"
            onClick={() => onCancelChanges()}
          >
            {__t(`${msgKey}.buttons.cancel`)}
          </ActionButton>
        )
      }
      {
        isEditMode && (
          <ActionButton
            color="primary"
            onClick={onSave}
          >
            {__t(`${msgKey}.buttons.${isForBrand || isInsights ? 'save' : 'send'}`)}
          </ActionButton>
        )
      }
      {
        !isEditMode && !isInsights && (
          <ActionButton
            color="primary"
            onClick={onSend}
          >
            {__t(`${msgKey}.buttons.send`)}
          </ActionButton>
        )
      }
    </React.Fragment>
  );

  const hasClientAddedNewFeedback = attachments.some(attachment => attachment.hasClientProvidedFeedback)
    || hasClientProvidedCaptionFeedback;

  const isZalandoOutfit = deliverable.deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT;
  const isEntertainmentVideo = deliverable.deliverableType === DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO;

  return (
    <React.Fragment>
      {(!isZalandoOutfit && !isEntertainmentVideo) && renderButtons()}
      <div className="deliverable-tab-footer__context-menu">
        <DeliverableMediaContextMenu
          key={`deliverable-tab-footer__context-menu--${deliverable.id}`}
          isBulk
          width={15}
          deliverable={deliverable}
          isEditMode={isEditMode}
          attachments={attachments}
          isInsights={isInsights}
          allowContentMove={allowContentMove}
          captionStatus={captionStatus}
          hasClientAddedNewFeedback={hasClientAddedNewFeedback}
          isCcm={isCcm}
          onUploadModeToggled={() => onEditModeToggle(!isEditMode)}
          onDownloadAttachments={onDownloadAttachments}
          onResetApprovalStatus={onResetApprovalStatus}
          onDeleteAttachments={onDeleteAttachments}
          onReleaseToClient={onReleaseToClient}
          onOpenMoveContent={onOpenMoveContent}
          onApplyClientFeedback={onApplyClientFeedback}
          onDeleteOutfits={onDeleteOutfits}
        />
      </div>
    </React.Fragment>
  );
};

export default DeliverableTabFooter;
